import { AmberApi } from './../../../core/models/api-helpers';
import { Component, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';

import { Observable, Subject, ReplaySubject, BehaviorSubject } from 'rxjs';

import { RxComponent, IndexWrapper } from 'app/core/components/common/rx.component';

declare var $: any;

@Component({
    selector: 'itpx-flexible-dropdown',
    templateUrl: './flexible-dropdown.component.html',
    styleUrls: ['./flexible-dropdown.component.css']
})
export class FlexibleDropdownComponent extends RxComponent {

    private readonly EMPTY = {};

    private form: FormGroup;
    @ViewChild('formContainer') formElement;

    @Input()
    private outerForm: FormGroup;
    @Input()
    private name: string;
    @Input()
    private config: FlexibleDropdownConfig;

    /** Invoke on toggle event - 'add', 'edit', null (collapsed state) */
    private toggleFormClick = new Subject<string>();
    /** Invoke when user submits new record */
    private addNewSubmitted = new Subject<any>();
    /** Invoke when user submits edit of record */
    private editSubmitted = new Subject<any>();
    /** Invoke when user changes selection */
    private itemChanged = new Subject<string>();

    /** Emits when item has been sucessfully added */
    @Output()
    private success = new EventEmitter<any>();
    /** Emits the item key (possibly a new one) when an existing item has been sucessfully edited */
    @Output()
    private edited = new EventEmitter<string>();
    /** Emits when selection has been changed */
    @Output()
    private selectChange = new EventEmitter<any>();
    /** Emits when the toggle state has changed */
    private formToggled: Observable<string>;
    /** Emits the items dictionary */
    private items: Observable<Array<{key: string, value: string}>>;
    /** Emits the currently selected item */
    private currentItem: Observable<string>;
    private loading: boolean;

    constructor(private fb: FormBuilder) {
        super();
    }

    ngOnInit() {

        this.buildForm(this.config.questions);

        let itemAdded = this.addNewSubmitted
            .flatMap(formData => this.addItem(formData))
            .shareReplay();

        let itemEdited = this.editSubmitted
            .flatMap(item => this.editItem(item))
            .shareReplay();

        this.currentItem = Observable.merge(
                this.itemChanged,
                itemAdded.map(item => item.key),
                itemEdited.switchMap(item => this.items.skip(1).take(1).mapTo(item.key))
            )
            .startWith(this.getInitialSelection())
            .shareReplay();

        this.currentItem
            .skipWhile(item => item === null)
            .mapWithIndex()
            .bindToLifecycle(this)
            .subscribe(item => {
                if (this.outerForm) {
                    let isInitialValue = item.index == 0;
                    let control = this.outerForm.controls[this.name];
                    control.setValue(item.value);
                    if (!isInitialValue) control.markAsDirty();
                }

                this.selectChange.emit(item.value);
            });

        this.formToggled = Observable.merge(
                this.toggleFormClick
                    .withLatestFrom(this.currentItem, (event, item) => ({event, item}))
                    .filter(({event, item}) => event !== 'edit' || item !== null)
                    .map(({event}) => event),
                itemAdded.mapTo(null),
                itemEdited.mapTo(null)
            )
            .startWith(null)
            .shareReplay();

        this.formToggled
            .skip(1)
            .bindToLifecycle(this)
            .subscribe(state => {
                this.form.reset();
                if (state) $(this.formElement.nativeElement).slideDown(250);
                else $(this.formElement.nativeElement).slideUp(250);
            });

        // Refresh dictionary on all these events
        this.items = Observable.merge(this.config.forceRefresh || Observable.never(), itemAdded, itemEdited)
            .startWith({})
            .switchMap(_ => this.config.dictionaryFactory().neverError())
            .shareReplay();

        // Populate edit form
        this.formToggled
            .filter(event => event === 'edit')
            .withLatestFrom(this.currentItem, (_, item) => item)
            .skipWhile(item => item === null)
            .flatMap(item => this.config.detailsFactory(item).neverError())
            .bindToLifecycle(this)
            .subscribe(data => {
                for (let key of this.config.questions.map(q => q.key)) {
                    this.form.get(key).setValue(data[key]);
                }
            });

        itemAdded
            .bindToLifecycle(this)
            .subscribe(_ => this.success.emit(true));

        itemEdited
            .bindToLifecycle(this)
            .subscribe(key => this.edited.emit(key))
    }

    private buildForm(questions: any[]) {
        let form = this.fb.group({});
        for (let question of questions) {
            let control = new FormControl();
            if (question.required) {
                control.setValidators([Validators.required]);
            }

            form.addControl(question.key, control);
        }

        this.form = form;
    }

    private getInitialSelection(): string {
        return this.config.initialItem ? this.config.initialItem.key : null;
    }

    private addItem(data: any): Observable<any> {
        this.loading = true;
        return this.config.addFactory(data)
            .take(1)
            .always(() => this.loading = false)
            .neverError();
    }

    private editItem(data: any): Observable<any> {
        this.loading = true;
        return this.config.editFactory(data)
            .take(1)
            .always(() => this.loading = false)
            .neverError();
    }

    private onFormToggle(state?: string) {
        if (!this.outerForm || !this.outerForm.controls[this.name].disabled) {
            this.toggleFormClick.next(state);
        }
    }

    private onItemChange(key: string) {
        this.itemChanged.next(key);
    }

    private onSubmit({ value, valid }: { value: any, valid: boolean }, state: string) {
        if (state =='add') {
            this.addNewSubmitted.next(value);
        } else {
            this.editSubmitted.next(value);
        }
    }
}

export class FlexibleDropdownConfig {

    label: string;
    questions: Array<any>;
    tooltip: string;
    initialItem?: any = null;
    placeholder: string;
    dictionaryFactory: () => Observable<any[]>;
    addFactory: (formData: any) => Observable<any>;
    editFactory?: (formData: any) => Observable<any>;
    detailsFactory: (id: string) => Observable<any>;
    addLabel: string;
    editLabel: string;
    forceRefresh?: Observable<any>;
    required: boolean = false;
}



// WEBPACK FOOTER //
// ./src/app/shared/components/flexible-dropdown/flexible-dropdown.component.ts